import { createClient } from '@supabase/supabase-js';

import { UserManager, WebStorageStateStore } from 'oidc-client-ts';
import { z } from 'zod';

import { GoogleUserSchema } from '@/Pages/OauthCallback/constants';
import { handleBasename } from '@/utils/handle-basename.tsx';

import {
  apiLoginPath,
  backendAuthType,
  backendUrl,
  baseUrl,
  mixedLoginMethods,
} from './constants';
import { getAPIUrl } from './get-api-url';

export const validateGoogleUserInfoFromQueryParams = (
  queryParams: URLSearchParams
): string | null => {
  const userb64 = queryParams.get('user');
  if (!userb64) {
    return null;
  }
  return validateGoogleUserInfo(userb64);
};

export const validateGoogleUserInfo = (userb64: string): string | null => {
  // Convert from url encoded b64 to stdencoding if needed
  const stdEncodedUserInfo = userb64.replace(/-/g, '+').replace(/_/g, '/');

  try {
    // check that the user has the required fields

    const payload = GoogleUserSchema.safeParse(
      JSON.parse(atob(stdEncodedUserInfo))
    );
    if (!payload.success) return null;
  } catch (e) {
    console.error('Error parsing user JSON:', e);
    return null;
  }
  return stdEncodedUserInfo;
};

export const supabase =
  backendAuthType === 'supabase'
    ? createClient(
        import.meta.env.VITE_SUPABASE_URL ?? '',
        import.meta.env.VITE_SUPABASE_KEY ?? ''
      )
    : undefined;

export const oauthManager =
  backendAuthType === 'oauth'
    ? new UserManager({
        authority: import.meta.env.VITE_OAUTH_ISSUER_URL ?? '',
        client_id: import.meta.env.VITE_OAUTH_CLIENT_ID ?? '',
        redirect_uri:
          window.location.origin + handleBasename('/oauth-callback', baseUrl),
        monitorSession: true,
        loadUserInfo: true,
        automaticSilentRenew: true,
        includeIdTokenInSilentRenew: true,
        silentRequestTimeoutInSeconds: 2,
        userStore: new WebStorageStateStore({
          prefix: 'ls:',
          store: window.localStorage,
        }),
        scope: 'openid email profile',
      })
    : undefined;

export const oauthProviderSpecificManager = mixedLoginMethods.customOidc
  ? (() => {
      interface SessionType {
        userInfo: string;
        user: { id: string; email: string; avatarUrl: string };
      }

      function parseSession(
        encodedUserInfo: string | null
      ): SessionType | null {
        if (!encodedUserInfo) return null;
        const payload = GoogleUserSchema.safeParse(
          JSON.parse(atob(encodedUserInfo))
        );
        if (!payload.success) return null;
        if (Date.parse(payload.data.expiresAt) < Date.now()) {
          fetch(`${getAPIUrl()}/oauth/custom-oidc/current-user`, {
            method: 'GET',
            credentials: 'include', // if you need to include cookies
            headers: {
              'Content-Type': 'application/json',
            },
          }).then((response) => {
            if (!response.ok) {
              console.error(
                'Failed to refresh user info, logging out.',
                response.statusText
              );
              writeSession(null);
              window.location.href = `${getAPIUrl()}/oauth/custom-oidc/logout`;
              return null;
            }
            response.json().then((responseJson) => {
              // Validate refreshed user
              const userb64 = responseJson['user'];
              const safeUser = validateGoogleUserInfo(userb64);
              if (!safeUser) return null;
              const newPayload = GoogleUserSchema.safeParse(
                JSON.parse(atob(safeUser))
              );
              if (!newPayload.success) return null;
              const session = {
                userInfo: safeUser,
                user: {
                  id: newPayload.data.userId,
                  email: newPayload.data.email,
                  avatarUrl: newPayload.data.avatarUrl,
                },
              };
              writeSession(session);
              return session;
            });
          });
        }

        return {
          userInfo: encodedUserInfo,
          user: {
            id: payload.data.userId,
            email: payload.data.email,
            avatarUrl: payload.data.avatarUrl,
          },
        };
      }

      let session: SessionType | null = null;
      const callbacks: ((newState: SessionType | null) => void)[] = [];

      // try to load the session from the session storage
      session = parseSession(
        window.localStorage.getItem('ls:auth:custom-oidc')
      );

      const writeSession = (newSession: SessionType | null) => {
        if (newSession) {
          window.localStorage.setItem(
            'ls:auth:custom-oidc',
            newSession.userInfo
          );
        } else {
          window.localStorage.removeItem('ls:auth:custom-oidc');
        }

        session = newSession;
        callbacks.forEach((cb) => cb(session));
      };

      return {
        signIn: () => {
          // hit and render backend API /api/v1/oauth/custom-oidc without using oauthManager,
          // which doesn't support the Google server-side flow
          // TODO: make sure api path is valid in all cases
          window.location.href = `${getAPIUrl()}/oauth/custom-oidc`;
        },
        register: (callback: (typeof callbacks)[number]) => {
          callbacks.push(callback);
          callback(session);

          return () => {
            const index = callbacks.indexOf(callback);
            if (index !== -1) {
              callbacks.splice(index, 1);
            }
          };
        },
        signOut: () => {
          writeSession(null);
          window.location.href = `${getAPIUrl()}/oauth/custom-oidc/logout`;
        },
        getUserInfo: () => {
          // check if the token didn't expire
          if (session && !parseSession(session.userInfo)) writeSession(null);
          return session?.userInfo;
        },
        setUserInfo: () => {
          session = parseSession(
            window.localStorage.getItem('ls:auth:custom-oidc')
          );
          if (session && parseSession(session.userInfo)) writeSession(session);
        },
      };
    })()
  : undefined;

export const basicManager =
  mixedLoginMethods.basic && !mixedLoginMethods.customOidc
    ? (() => {
        const JWTSchema = z.object({
          email: z.string(),
          full_name: z.string(),
          sub: z.string(),
          exp: z.number(),
        });

        interface SessionType {
          token: string;
          user: { id: string; email: string };
        }

        function parseSession(token: string | null): SessionType | null {
          if (!token) return null;
          const payload = JWTSchema.safeParse(
            JSON.parse(atob(token.split('.')[1]))
          );

          if (!payload.success) return null;
          if (payload.data.exp * 1000 < Date.now()) return null;
          return {
            token,
            user: { id: payload.data.sub, email: payload.data.email },
          };
        }

        let session: SessionType | null = null;
        const callbacks: ((newState: SessionType | null) => void)[] = [];

        // try to load the session from the session storage
        session = parseSession(window.localStorage.getItem('ls:auth:mixed'));

        const writeSession = (newSession: SessionType | null) => {
          if (newSession) {
            window.localStorage.setItem('ls:auth:mixed', newSession.token);
          } else {
            window.localStorage.removeItem('ls:auth:mixed');
          }

          session = newSession;
          callbacks.forEach((cb) => cb(session));
        };

        return {
          signIn: async (username: string, password: string) => {
            const req = await fetch(`${backendUrl}${apiLoginPath}`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Basic ${btoa(`${username}:${password}`)}`,
              },
            });

            if (!req.ok) {
              try {
                const body = (await req.json()) as { error: string };
                throw new Error(body.error);
              } catch {
                throw new Error('Failed to login');
              }
            }

            const body = (await req.json()) as { access_token: string };
            writeSession(parseSession(body.access_token));
          },
          register: (callback: (typeof callbacks)[number]) => {
            callbacks.push(callback);
            callback(session);

            return () => {
              const index = callbacks.indexOf(callback);
              if (index !== -1) {
                callbacks.splice(index, 1);
              }
            };
          },
          signOut: () => writeSession(null),
          getAccessToken: () => {
            // check if the token didn't expiry
            if (session && !parseSession(session.token)) writeSession(null);
            return session?.token;
          },
        };
      })()
    : undefined;

export const desktopAuthManager =
  backendAuthType === 'desktop'
    ? (() => {
        const getDesktopToken = () => {
          try {
            return z
              .object({ apiKey: z.string(), apiUrl: z.string() })
              .parse(
                JSON.parse(window.localStorage.getItem('ls:desktop:auth') ?? '')
              );
          } catch {
            throw new Error('Not authenticated');
          }
        };
        return {
          resolveUrl: (url: string | URL) => {
            const auth = getDesktopToken();
            let path = typeof url === 'string' ? url : url.pathname;
            if (path.startsWith('/')) path = path.slice(1);
            return new URL(`${auth.apiUrl}/${path}`);
          },
          getAuthHeaders: (): Promise<Record<string, string>> => {
            const auth = getDesktopToken();
            return Promise.resolve({ 'X-Api-Key': auth.apiKey });
          },
        };
      })()
    : undefined;
